/**
 * The list pages' shared pager. Every list endpoint in this app uses DRF's
 * standard `{count, next, previous, results}` envelope with a fixed
 * `PAGE_SIZE` of 20 (src/api/types.ts) — so unlike Carbon's `Pagination`
 * default (variable page size, chosen by the viewer), this app has exactly
 * one page size and no reason to offer a picker for it.
 *
 * This is also a genuine upgrade over the "Previous / Page N / Next" pager it
 * replaces: Carbon's page-number select lets a caller jump straight to any
 * page, not just step one at a time, and DRF's `page` query param already
 * supports that random access — nothing on the query-function side changes.
 */
import { Pagination } from '@carbon/react';

export interface PagerProps {
  /** 1-indexed, matching both `useState`'s `page` and DRF's `page` query param. */
  page: number;
  /** The paginated envelope's `count` — total items, not total pages. */
  count: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
}

export function Pager({ page, count, onPageChange, pageSize = 20 }: PagerProps): React.JSX.Element {
  return (
    <Pagination
      page={page}
      pageSize={pageSize}
      pageSizes={[pageSize]}
      pageSizeInputDisabled
      totalItems={count}
      onChange={(data) => onPageChange(data.page)}
    />
  );
}

export default Pager;
